import React, { Fragment, FunctionComponent, useCallback, useEffect, useState } from "react";
import styles from "./MediaCloudPage.module.scss";
import MediaCloudPageContent from "./MediaCloudPageContent.model";

import { OPEN_FACETS_MENU, OPEN_MEDIA_CLOUD_DOWNLOAD_OVERLAY, OPEN_MEDIA_CLOUD_EMAIL_OVERLAY } from "layout/redux/overlay.actions";
import { useDispatch, useSelector } from "react-redux";
import combineClasses from "helpers/combine-classes";
import Dictionary from "components/Dictionary/Dictionary";
import IconViewer from "components/IconViewer/IconViewer";
import useCategories from "./hooks/useCategories";
import useWindowDimensions from "hooks/useWindowDimensions";
import Sidemenu from "./components/Sidemenu/Sidemenu";
import Spinner from "components/Spinner/Spinner";
import MediaCloudItem from "pages/Catalog/components/MediaCloudItem/MediaCloudItem";
import useTranslation from "components/Dictionary/hooks/useTranslation";
import isBrowserContext from "helpers/isBrowserContext";
import urlBuilder from "app/umbraco/helpers/urlBuilder";

const MediaCloudPage: FunctionComponent<MediaCloudPageContent> = (props) => {
    const [query, setQuery] = useState<string>("");
    const [tags, setTags] = useState<string[]>([]);
    const [categories, viewState] = useCategories(tags, query);

    const [selectedItems, setSelectedItems] = useState<any[]>([]);

    const dispatch = useDispatch();
    const { width } = useWindowDimensions();
    const overlay = useSelector((state: any) => state.overlay);

    const translations = useTranslation("MEDIA_CLOUD.SEARCH_PLACEHOLDER", "MEDIA_CLOUD.DOWNLOAD_CHOSEN_ERROR") as Record<string, string>;

    const [showSidemenu, setShowSidemenu] = useState<boolean>(false);
    const menuViewState = width >= 768 ? "FIXED" : width >= 450 ? "FLOAT" : "FULL";
    const toggleMenuClass = showSidemenu && overlay.currentOverlay === "FACETS_MENU" ? styles.is_open : undefined;

    useEffect(() => {
        if (width >= 768) {
            setShowSidemenu(true);
        }
    }, [width]);

    const toggleMenu = useCallback(() => {
        if (showSidemenu && overlay.currentOverlay === "FACETS_MENU") {
            setShowSidemenu(false);
        } else {
            dispatch({ type: OPEN_FACETS_MENU });
            setShowSidemenu(true);
        }
    }, [showSidemenu, overlay]);

    const onTagsChanged = useCallback(
        (tags: any) => {
            setTags(tags);
        },
        [setTags]
    );

    const onCheckboxChange = (event: any, value: any) => {
        let result: any[] = JSON.parse(JSON.stringify(selectedItems));

        if (event.target.checked === true) {
            result.push(value.id);
        } else {
            result = result.filter((x) => x !== value.id);
        }

        setSelectedItems(result);
    };

    const onDeselectSelectedItemsClick = (event: any) => {
        event?.preventDefault();

        // 1. Remove selection from state
        setSelectedItems([]);

        // 2. Uncheck all actual checkboxes in DOM
        if (isBrowserContext() && typeof document !== "undefined") {
            document.querySelectorAll(`input[type="checkbox"]`).forEach((element: Element) => {
                (element as HTMLInputElement).checked = false;
            });
        }
    };

    const onDownloadButtonClick = () => {
        if (selectedItems && selectedItems.length > 0) {
            const url = urlBuilder(`/api/file/getzip?ids=${selectedItems.join(",")}`).toString();

            if (isBrowserContext()) {
                const tempAnchor = document.createElement("a");
                tempAnchor.href = url;
                tempAnchor.download = "media_cloud";
                document.body.appendChild(tempAnchor);
                tempAnchor.click();
                document.body.removeChild(tempAnchor);
            }
        } else {
            alert(translations["MEDIA_CLOUD.DOWNLOAD_CHOSEN_ERROR"]);
        }
    };

    const onEmailButtonClick = () => {
        const url = urlBuilder(`/api/file/getzip?ids=${selectedItems.join(",")}`).toString();

        dispatch({
            type: OPEN_MEDIA_CLOUD_EMAIL_OVERLAY,
            content: {
                url: url,
            },
        });
    };

    return (
        <div className={styles.inner}>
            {/* Toggle */}
            {width < 768 ? (
                <button
                    className={combineClasses([styles.menu_toggle, toggleMenuClass])}
                    onClick={() => toggleMenu()}
                >
                    <div className={styles.menu_toggle_inner}>
                        <Dictionary name="MEDIA_CLOUD.SHOW_TAGS" />
                        <IconViewer
                            className={styles.chevron}
                            name="chevronDown"
                            heightRatio={7.805 / 12.781}
                        />
                    </div>
                </button>
            ) : null}

            {/* Sidemenu */}
            {showSidemenu === true && ((menuViewState === "FULL" && overlay.currentOverlay === "FACETS_MENU") || menuViewState !== "FULL") ? (
                <Sidemenu
                    view={menuViewState}
                    tags={tags}
                    onTagsChange={(tags) => onTagsChanged(tags)}
                />
            ) : null}

            <div className={styles.container}>
                <h1 className={styles.headline}>
                    <Dictionary name="MEDIA_CLOUD.HEADLINE" />
                </h1>
                <label className={styles.search_wrapper}>
                    <IconViewer
                        className={styles.search_icon}
                        name="search"
                    />
                    <input
                        type="text"
                        className={styles.search_input}
                        placeholder={translations["MEDIA_CLOUD.SEARCH_PLACEHOLDER"]}
                        onChange={(event) => setQuery(event.target.value)}
                    />
                </label>

                <div className={styles.toolbar}>
                    <button
                        className={styles.toolbar_button}
                        disabled={selectedItems.length === 0}
                        onClick={(event) => onDeselectSelectedItemsClick(event)}
                    >
                        <IconViewer
                            className={styles.toolbar_button_icon}
                            name="download"
                        />
                        <Dictionary name="MEDIA_CLOUD.DESELECT_ALL" />
                    </button>

                    <button
                        className={styles.toolbar_button}
                        disabled={selectedItems.length === 0}
                        onClick={() => onDownloadButtonClick()}
                    >
                        <IconViewer
                            className={styles.toolbar_button_icon}
                            name="download"
                        />
                        <Dictionary name="MEDIA_CLOUD.DOWNLOAD_CHOSEN" />
                    </button>

                    <button
                        className={styles.toolbar_button}
                        disabled={selectedItems.length === 0}
                        onClick={() => onEmailButtonClick()}
                    >
                        <IconViewer
                            className={styles.toolbar_button_icon}
                            name="mail"
                        />
                        <Dictionary name="MEDIA_CLOUD.EMAIL_CHOSEN" />
                    </button>
                </div>

                {viewState === "LOADING" ? <Spinner className={styles.spinner} /> : null}

                {/* List */}
                {viewState === "LOADED" ? (
                    <div className={styles.content}>
                        {categories?.map((category: any, catIndex: number) => {
                            return (
                                <div
                                    className={styles.category}
                                    key={catIndex}
                                >
                                    <h1 className={styles.category_heading}>{category.category.primaryTitle}</h1>
                                    {category.products?.map((item: any, prodIndex: number) => {
                                        return (
                                            <div className={styles.row}>
                                                <span className={styles.product_tagline}>{item.sku}</span>
                                                <h1 className={styles.product_heading}>{item.itemName}</h1>
                                                <div
                                                    className={styles.list}
                                                    key={`${category.id}_${prodIndex}`}
                                                >
                                                    {item.mediaCloudImage1 ? (
                                                        <MediaCloudItem
                                                            className={styles.item}
                                                            {...item.mediaCloudImage1}
                                                            product={item}
                                                        >
                                                            <label className={styles.checkbox_wrapper}>
                                                                <input
                                                                    className={styles.checkbox_input}
                                                                    type="checkbox"
                                                                    onChange={(event) => onCheckboxChange(event, item.mediaCloudImage1)}
                                                                />
                                                                <span className={styles.checkbox_inner}></span>
                                                            </label>
                                                        </MediaCloudItem>
                                                    ) : null}

                                                    {item.mediaCloudImage2 ? (
                                                        <MediaCloudItem
                                                            className={styles.item}
                                                            {...item.mediaCloudImage2}
                                                            product={item}
                                                        >
                                                            <label className={styles.checkbox_wrapper}>
                                                                <input
                                                                    className={styles.checkbox_input}
                                                                    type="checkbox"
                                                                    onChange={(event) => onCheckboxChange(event, item.mediaCloudImage2)}
                                                                />
                                                                <span className={styles.checkbox_inner}></span>
                                                            </label>
                                                        </MediaCloudItem>
                                                    ) : null}

                                                    {item.mediaCloudImage3 ? (
                                                        <MediaCloudItem
                                                            className={styles.item}
                                                            {...item.mediaCloudImage3}
                                                            product={item}
                                                        >
                                                            <label className={styles.checkbox_wrapper}>
                                                                <input
                                                                    className={styles.checkbox_input}
                                                                    type="checkbox"
                                                                    onChange={(event) => onCheckboxChange(event, item.mediaCloudImage3)}
                                                                />
                                                                <span className={styles.checkbox_inner}></span>
                                                            </label>
                                                        </MediaCloudItem>
                                                    ) : null}

                                                    {item.mediaCloudImage4 ? (
                                                        <MediaCloudItem
                                                            className={styles.item}
                                                            {...item.mediaCloudImage4}
                                                            product={item}
                                                        >
                                                            <label className={styles.checkbox_wrapper}>
                                                                <input
                                                                    className={styles.checkbox_input}
                                                                    type="checkbox"
                                                                    onChange={(event) => onCheckboxChange(event, item.mediaCloudImage4)}
                                                                />
                                                                <span className={styles.checkbox_inner}></span>
                                                            </label>
                                                        </MediaCloudItem>
                                                    ) : null}

                                                    {item.mediaCloudA4Skilt1 ? (
                                                        <MediaCloudItem
                                                            className={styles.item}
                                                            {...item.mediaCloudA4Skilt1}
                                                            product={item}
                                                        >
                                                            <label className={styles.checkbox_wrapper}>
                                                                <input
                                                                    className={styles.checkbox_input}
                                                                    type="checkbox"
                                                                    onChange={(event) => onCheckboxChange(event, item.mediaCloudA4Skilt1)}
                                                                />
                                                                <span className={styles.checkbox_inner}></span>
                                                            </label>
                                                        </MediaCloudItem>
                                                    ) : null}

                                                    {item.mediaCloudA4Skilt2 ? (
                                                        <MediaCloudItem
                                                            className={styles.item}
                                                            {...item.mediaCloudA4Skilt2}
                                                            product={item}
                                                        >
                                                            <label className={styles.checkbox_wrapper}>
                                                                <input
                                                                    className={styles.checkbox_input}
                                                                    type="checkbox"
                                                                    onChange={(event) => onCheckboxChange(event, item.mediaCloudA4Skilt2)}
                                                                />
                                                                <span className={styles.checkbox_inner}></span>
                                                            </label>
                                                        </MediaCloudItem>
                                                    ) : null}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                ) : null}

                {viewState === "EMPTY" ? (
                    <div className={styles.empty}>
                        <Dictionary name="MEDIA_CLOUD.EMPTY" />
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default MediaCloudPage;
